/**
 * 이상한 방 - 소품 메시 팩토리 (catalogId → THREE.Object3D)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 4.2절(무변화 회전) · 8절(카탈로그 규격) · `ASSETS.md`
 *
 * ## 이 파일이 지는 책임 하나
 *
 * **선언된 `rotSymmetry`와 실제 형상의 대칭 차수가 어긋나지 않게 하는 것.**
 * `rotSymmetry: 1`이라 해 놓고 정육면체로 그리면 서버는 회전을 승인하는데 화면은 그대로다 -
 * 술래가 원리적으로 찾을 수 없는 정답(원칙 2 위반). 반대로 `4`라 해 놓고 비대칭으로 그리면
 * 만들 수 있는 변화를 못 만들게 된다.
 *
 * 그래서 형상을 **정규화 파트 목록이라는 데이터**로 정의했다. 덕분에 그 목록을 90°/180° 돌려
 * 자기 자신과 비교하는 검사를 테스트가 전 소품에 돌릴 수 있다(`meshFactory.test.ts`).
 *
 * ## 성능 - 500개를 놓는다는 전제
 *
 * 20m × 20m 실내에 300~500개가 들어간다. 그래서 **지오메트리는 단위 3종(박스·원기둥·구)뿐**이고
 * 크기는 전부 `mesh.scale`로 낸다. 머티리얼은 (슬롯 × colorId)로 캐시하므로 최대 60개다.
 * 인스턴스마다 새로 할당되는 것은 `Mesh` 껍데기뿐이다.
 *
 * ### InstancedMesh를 쓰지 않은 이유
 *
 * 검토했고 기각했다. 이 게임의 오브젝트는 각자 다른 색으로 바뀌고, 개별적으로 추가·제거되며,
 * 레이캐스트로 **하나씩 식별**돼야 한다(6.1절 오브젝트 히트). 인스턴싱으로 가면
 * `instanceId → InstanceId` 역인덱스를 직접 유지해야 하는데, 그게 한 번이라도 어긋나면
 * **엉뚱한 오브젝트가 정답 처리된다.** 프레임 몇 개와 바꿀 위험이 아니다.
 * 공유만으로 메모리와 GC 압력은 대부분 해결되며, 프레임이 실제로 모자라면
 * "정적 소품만 인스턴싱, 변화 대상은 개별 메시"로 나누는 것이 다음 수순이다.
 *
 * ## 에셋(GLB)과 절차적 형상의 관계
 *
 * 기본은 Kenney CC0 GLB다(`ASSETS.md`). 로드는 **비동기**이고 실패할 수 있으므로
 * 절차적 형상이 항상 존재하는 폴백이다 - 에셋 하나가 깨졌다고 화면이 비면 안 된다.
 * `preloadCatalogModels()`를 부르지 않으면 전부 절차적 형상으로 그려지며, 그래도 게임은 성립한다.
 *
 * 파일 길이의 절반은 레시피 표다. 소품이 더 늘면 표를 `shapeRecipes.ts`로 떼는 것이 다음 수순이다.
 */

import {
  Box3,
  BoxGeometry,
  Color,
  CylinderGeometry,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  SphereGeometry,
  Vector3,
  type BufferGeometry,
  type Object3D,
} from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'

import { CATALOG } from '../catalog/catalog'
import { getColor } from '../catalog/palette'
import { CELL_SIZE_METERS, type CatalogEntry, type CatalogId, type PaletteIndex } from '../core/types'

/* --- 파트 스펙: 정규화 좌표계 ---------------------------------------------------- */

/** 단위 지오메트리 종류. 이 3종의 조합만으로 카탈로그 전체를 그린다. */
export type PartShape = 'box' | 'cylinder' | 'sphere'

/**
 * 파트의 색 슬롯.
 *  - `main`/`dark`/`light` : 팔레트 색에서 파생. **색 변경 동사가 실제로 보이는 면적**이다
 *  - `metal`/`leaf`        : 고정색. 금속 다리와 잎까지 팔레트색으로 칠하면 물건이 단색 덩어리가
 *                            되어 실루엣 판독이 나빠진다(ASSETS.md 3.1절과 같은 이유)
 *  - `marker`              : 방향 표식 전용. 몸통 색과 반대쪽으로 대비를 잡는다(4.2절 부수 요건)
 */
export type PartSlot = 'main' | 'dark' | 'light' | 'metal' | 'leaf' | 'marker'

/**
 * 파트 하나. 좌표·크기는 **footprint 박스를 [0,1]³로 정규화한 값**이다.
 * 정규화 덕분에 같은 레시피를 크기가 다른 소품에 쓸 수 있고, 무엇보다 0~1만 지키면
 * "보이는 부피 = 서버가 점유했다고 판단한 셀"이 자동으로 성립한다.
 */
export type PartSpec = Readonly<{
  shape: PartShape
  slot: PartSlot
  center: Readonly<{ x: number; y: number; z: number }>
  size: Readonly<{ x: number; y: number; z: number }>
}>

/** 소품 하나의 형상 정의. `model`은 있으면 쓰고 없거나 로드 실패면 `parts`로 그린다. */
export type ShapeRecipe = Readonly<{
  /** `public/assets/models/<model>.glb`. 로드 실패 시 조용히 `parts`로 폴백한다. */
  model?: string
  parts: readonly PartSpec[]
}>

type Triple = readonly [number, number, number]

function spec(shape: PartShape, slot: PartSlot, center: Triple, size: Triple): PartSpec {
  return {
    shape,
    slot,
    center: { x: center[0], y: center[1], z: center[2] },
    size: { x: size[0], y: size[1], z: size[2] },
  }
}

const box = (slot: PartSlot, center: Triple, size: Triple): PartSpec => spec('box', slot, center, size)
const cyl = (slot: PartSlot, center: Triple, size: Triple): PartSpec =>
  spec('cylinder', slot, center, size)
const sph = (slot: PartSlot, center: Triple, size: Triple): PartSpec =>
  spec('sphere', slot, center, size)

/* --- 형상 패밀리: 여러 소품이 공유하는 구조 --------------------------------------- */

/** 다리 4개. 네 귀퉁이 대칭이라 **방향 정보를 만들지 않는다** - 방향은 등받이·문짝·서랍이 만든다. */
function legs(slot: PartSlot, inset: number, height: number, thickness: number): PartSpec[] {
  const near = inset + thickness / 2
  const far = 1 - near
  return [near, far].flatMap((u) =>
    [near, far].map((w) => box(slot, [u, height / 2, w], [thickness, height, thickness])),
  )
}

/** 십자로 뻗은 팔 4개(코트걸이). 90° 회전에 대해 자기 자신으로 돌아온다. */
function crossArms(slot: PartSlot, y: number, reach: number, thickness: number): PartSpec[] {
  const offset = 1 - reach / 2
  return [
    box(slot, [offset, y, 0.5], [reach, thickness, thickness]),
    box(slot, [1 - offset, y, 0.5], [reach, thickness, thickness]),
    box(slot, [0.5, y, offset], [thickness, thickness, reach]),
    box(slot, [0.5, y, 1 - offset], [thickness, thickness, reach]),
  ]
}

/** 앞이 열린 책장. 옆판 2 + 뒤판 + 선반 n. 뒤판이 −z에 있으므로 네 방향이 전부 다르다. */
function openShelf(shelfCount: number): PartSpec[] {
  const shelves: PartSpec[] = []
  for (let index = 0; index < shelfCount; index += 1) {
    const y = 0.04 + (index * 0.92) / (shelfCount - 1)
    shelves.push(box('main', [0.5, y, 0.56], [0.86, 0.05, 0.82]))
  }
  return [
    box('main', [0.05, 0.5, 0.5], [0.1, 1, 1]),
    box('main', [0.95, 0.5, 0.5], [0.1, 1, 1]),
    box('dark', [0.5, 0.5, 0.06], [1, 1, 0.12]),
    ...shelves,
  ]
}

/** 문짝 달린 수납장. 문이 +z를 보므로 180° 회전이 반드시 보인다. */
function cabinet(doorCount: number, plinth: number): PartSpec[] {
  const bodyHeight = 1 - plinth
  const doors: PartSpec[] = []
  for (let index = 0; index < doorCount; index += 1) {
    const u = (index + 0.5) / doorCount
    const handleSide = u < 0.5 ? 1 : -1
    doors.push(box('light', [u, plinth + bodyHeight / 2, 0.95], [0.92 / doorCount, bodyHeight * 0.86, 0.1]))
    doors.push(
      box('metal', [u + (handleSide * 0.4) / doorCount, plinth + bodyHeight / 2, 0.93], [0.04, bodyHeight * 0.3, 0.08]),
    )
  }
  return [
    box('main', [0.5, plinth + bodyHeight / 2, 0.48], [1, bodyHeight, 0.96]),
    box('dark', [0.5, plinth / 2, 0.5], [0.94, plinth, 0.94]),
    ...doors,
  ]
}

/** 화면이 +z를 보는 판때기(TV·모니터·거울). 스탠드 유무만 다르다. */
function screenPanel(panelTop: number, standHeight: number): PartSpec[] {
  const panelHeight = panelTop - standHeight
  const panelY = standHeight + panelHeight / 2
  const stand: PartSpec[] =
    standHeight > 0
      ? [
          box('dark', [0.5, standHeight / 2, 0.42], [0.24, standHeight, 0.3]),
          box('dark', [0.5, 0.03, 0.5], [0.66, 0.06, 0.86]),
        ]
      : []
  return [
    box('dark', [0.5, panelY, 0.32], [1, panelHeight, 0.44]),
    box('light', [0.5, panelY, 0.62], [0.9, panelHeight * 0.84, 0.18]),
    ...stand,
  ]
}

/** 원기둥 화분 + 잎 뭉치. 전부 축 위에 있으므로 완전 대칭(rotSymmetry 4)이다. */
function plant(potHeight: number): PartSpec[] {
  const foliageBase = potHeight + (1 - potHeight) * 0.35
  const topFoliage = (1 - potHeight) * 0.42
  return [
    cyl('dark', [0.5, potHeight / 2, 0.5], [0.82, potHeight, 0.82]),
    sph('leaf', [0.5, foliageBase, 0.5], [1, (1 - potHeight) * 0.8, 1]),
    sph('leaf', [0.5, 1 - topFoliage / 2, 0.5], [0.62, topFoliage, 0.62]),
  ]
}

/** 받침 + 기둥 + 갓. 램프 계열. 역시 축 대칭이라 회전이 잠긴다. */
function lamp(shadeHeight: number): PartSpec[] {
  const poleTop = 1 - shadeHeight
  return [
    cyl('dark', [0.5, 0.03, 0.5], [0.86, 0.06, 0.86]),
    cyl('metal', [0.5, poleTop / 2, 0.5], [0.12, poleTop, 0.12]),
    cyl('light', [0.5, 1 - shadeHeight / 2, 0.5], [0.95, shadeHeight, 0.95]),
  ]
}

/* --- 레시피 표 ------------------------------------------------------------------ */

/**
 * catalogId → 형상.
 *
 * 읽는 법: 숫자는 전부 footprint 대비 비율(0~1)이다. `[0.5, 0.5, 0.5]`는 정중앙,
 * z가 1에 가까울수록 **정면(+z)**이다. 회전 0단계에서 +z가 정면이라는 규약은
 * objectView.ts의 방향 표식(`MARKER_DIRECTIONS[0] = +z`)과 같다.
 *
 * `model`은 `public/assets/models/` 의 Kenney GLB 파일명이다.
 *
 * 이 표가 카탈로그의 `rotSymmetry` 선언과 어긋나지 않는지는 `meshFactory.test.ts`가
 * 파트 목록을 90°/180° 돌려 자기 자신과 대조해 검사한다(4.2절).
 */
export const SHAPE_RECIPES: ReadonlyMap<CatalogId, ShapeRecipe> = new Map<CatalogId, ShapeRecipe>([
  /* --- 초소형 -------------------------------------------------------------------- */
  ['mug', { model: 'mug', parts: [
    cyl('main', [0.45, 0.42, 0.5], [0.72, 0.84, 0.72]),
    cyl('light', [0.45, 0.8, 0.5], [0.56, 0.1, 0.56]),
    // 손잡이가 방향의 전부다. 이게 없으면 rotSymmetry 1 선언이 거짓이 된다.
    box('main', [0.9, 0.5, 0.5], [0.16, 0.4, 0.14])] }],
  ['book', { model: 'book', parts: [
    box('main', [0.5, 0.4, 0.54], [0.82, 0.78, 0.64]),
    box('dark', [0.5, 0.42, 0.16], [0.86, 0.84, 0.12])] }],
  ['trash-bin', { model: 'trashcan', parts: [
    cyl('main', [0.5, 0.44, 0.5], [0.8, 0.88, 0.8]),
    cyl('dark', [0.5, 0.93, 0.5], [0.92, 0.1, 0.92])] }],
  ['cup', { model: 'cup', parts: [
    cyl('main', [0.5, 0.4, 0.5], [0.62, 0.8, 0.62]),
    cyl('light', [0.5, 0.78, 0.5], [0.5, 0.08, 0.5])] }],
  ['plate', { model: 'plate-dinner', parts: [
    cyl('main', [0.5, 0.1, 0.5], [0.96, 0.16, 0.96]),
    cyl('light', [0.5, 0.2, 0.5], [0.7, 0.08, 0.7])] }],
  ['bottle', { model: 'bottle-oil', parts: [
    cyl('main', [0.5, 0.3, 0.5], [0.52, 0.6, 0.52]),
    cyl('main', [0.5, 0.72, 0.5], [0.24, 0.3, 0.24]),
    cyl('dark', [0.5, 0.92, 0.5], [0.28, 0.1, 0.28])] }],
  ['computer-mouse', { model: 'computer-mouse', parts: [
    sph('main', [0.5, 0.28, 0.5], [0.72, 0.56, 0.92]),
    box('dark', [0.5, 0.5, 0.72], [0.42, 0.12, 0.32])] }],

  /* --- 소형 ---------------------------------------------------------------------- */
  ['keyboard', { model: 'computer-keyboard', parts: [
    box('dark', [0.5, 0.28, 0.5], [1, 0.48, 0.92]),
    box('light', [0.5, 0.58, 0.62], [0.9, 0.16, 0.52])] }],
  ['doormat', { model: 'rug-doormat', parts: [
    box('main', [0.5, 0.42, 0.5], [1, 0.84, 1]),
    box('light', [0.5, 0.88, 0.5], [0.78, 0.24, 0.66])] }],
  ['pillow', { model: 'pillow', parts: [
    box('main', [0.5, 0.52, 0.5], [1, 0.9, 1]),
    box('light', [0.5, 0.06, 0.5], [0.88, 0.12, 0.88])] }],
  ['laptop', { model: 'laptop', parts: [
    box('dark', [0.5, 0.14, 0.62], [1, 0.28, 0.76]),
    box('main', [0.5, 0.53, 0.14], [0.96, 0.94, 0.16]),
    box('light', [0.5, 0.53, 0.24], [0.8, 0.76, 0.06])] }],
  ['microwave', { model: 'kitchen-microwave', parts: [
    box('main', [0.5, 0.5, 0.44], [1, 1, 0.88]),
    box('dark', [0.42, 0.5, 0.94], [0.7, 0.82, 0.12]),
    box('light', [0.88, 0.5, 0.94], [0.2, 0.82, 0.12])] }],
  ['lamp-table', { model: 'lamp-round-table', parts: lamp(0.44) }],
  ['nightstand', { model: 'cabinet-bed-drawer', parts: [
    box('main', [0.5, 0.58, 0.5], [1, 0.84, 1]),
    box('light', [0.5, 0.74, 0.96], [0.8, 0.26, 0.08]),
    box('light', [0.5, 0.4, 0.96], [0.8, 0.26, 0.08]),
    ...legs('dark', 0.06, 0.16, 0.12)] }],
  ['monitor', { model: 'computer-screen', parts: screenPanel(1, 0.26) }],
  ['cardboard-box', { model: 'cardboard-box-closed', parts: [
    box('main', [0.5, 0.46, 0.5], [1, 0.92, 1]),
    box('light', [0.5, 0.95, 0.5], [0.18, 0.1, 1]),
    // 송장 스티커. 이 한 장이 없으면 정육면체라 회전이 안 보인다.
    box('light', [0.5, 0.52, 0.97], [0.46, 0.3, 0.06])] }],
  ['stool-bar', { model: 'stool-bar', parts: [
    cyl('main', [0.5, 0.9, 0.5], [0.96, 0.16, 0.96]),
    cyl('metal', [0.5, 0.44, 0.5], [0.2, 0.8, 0.2]),
    cyl('metal', [0.5, 0.26, 0.5], [0.62, 0.05, 0.62]),
    cyl('dark', [0.5, 0.03, 0.5], [0.78, 0.06, 0.78])] }],
  ['speaker', { model: 'speaker', parts: [
    box('dark', [0.5, 0.5, 0.44], [1, 1, 0.88]),
    sph('light', [0.5, 0.68, 0.86], [0.6, 0.34, 0.24]),
    sph('light', [0.5, 0.3, 0.86], [0.42, 0.22, 0.24])] }],
  ['mirror', { model: 'bathroom-mirror', parts: [
    box('main', [0.5, 0.5, 0.34], [1, 1, 0.68]),
    box('light', [0.5, 0.5, 0.82], [0.82, 0.88, 0.36])] }],

  /* --- 중형 ---------------------------------------------------------------------- */
  ['chair', { model: 'chair', parts: [
    box('main', [0.5, 0.5, 0.5], [0.92, 0.1, 0.92]),
    box('main', [0.5, 0.78, 0.88], [0.92, 0.44, 0.14]),
    ...legs('dark', 0.04, 0.5, 0.12)] }],
  ['bench', { model: 'bench-cushion-low', parts: [
    box('main', [0.5, 0.78, 0.5], [1, 0.44, 1]),
    ...legs('dark', 0.04, 0.6, 0.14)] }],
  ['table-dining', { model: 'table-dining', parts: [
    box('main', [0.5, 0.9, 0.5], [1, 0.2, 1]),
    box('dark', [0.5, 0.74, 0.5], [0.9, 0.12, 0.9]),
    ...legs('dark', 0.05, 0.8, 0.1)] }],
  ['side-table', { model: 'side-table-drawers', parts: [
    box('main', [0.5, 0.92, 0.5], [1, 0.16, 1]),
    box('main', [0.5, 0.54, 0.5], [0.86, 0.6, 0.86]),
    box('light', [0.5, 0.62, 0.94], [0.7, 0.24, 0.1]),
    ...legs('dark', 0.06, 0.24, 0.1)] }],
  ['cabinet-tv', { model: 'cabinet-television', parts: [
    ...cabinet(2, 0.18)] }],
  ['television', { model: 'television-modern', parts: screenPanel(1, 0.2) }],
  ['desk', { model: 'desk', parts: [
    box('main', [0.5, 0.92, 0.5], [1, 0.16, 1]),
    // 서랍장이 한쪽에만 있다 → 180° 회전이 확실히 보인다.
    box('main', [0.82, 0.5, 0.5], [0.32, 0.68, 0.86]),
    box('light', [0.82, 0.66, 0.95], [0.26, 0.16, 0.08]),
    box('light', [0.82, 0.4, 0.95], [0.26, 0.16, 0.08]),
    box('dark', [0.4, 0.5, 0.08], [0.6, 0.5, 0.08]),
    box('dark', [0.06, 0.42, 0.12], [0.08, 0.84, 0.1]),
    box('dark', [0.06, 0.42, 0.88], [0.08, 0.84, 0.1])] }],
  ['kitchen-cabinet', { model: 'kitchen-cabinet-drawer', parts: cabinet(1, 0.14) }],
  ['kitchen-stove', { model: 'kitchen-stove', parts: [
    box('main', [0.5, 0.44, 0.5], [1, 0.88, 1]),
    box('dark', [0.5, 0.92, 0.5], [1, 0.08, 1]),
    box('light', [0.5, 0.44, 0.96], [0.86, 0.6, 0.08]),
    box('metal', [0.5, 0.92, 0.06], [0.9, 0.16, 0.08])] }],
  ['toilet', { model: 'toilet', parts: [
    cyl('main', [0.5, 0.34, 0.64], [0.86, 0.56, 0.68]),
    cyl('light', [0.5, 0.64, 0.62], [0.92, 0.08, 0.74]),
    box('main', [0.5, 0.6, 0.14], [0.8, 0.76, 0.28])] }],
  ['bathroom-sink', { model: 'bathroom-sink', parts: [
    cyl('main', [0.5, 0.3, 0.5], [0.46, 0.6, 0.46]),
    box('main', [0.5, 0.68, 0.5], [1, 0.2, 1]),
    cyl('light', [0.5, 0.76, 0.54], [0.7, 0.1, 0.66]),
    box('metal', [0.5, 0.86, 0.12], [0.12, 0.24, 0.12])] }],
  ['chair-desk', { model: 'chair-desk', parts: [
    box('main', [0.5, 0.52, 0.5], [0.9, 0.1, 0.9]),
    box('main', [0.5, 0.77, 0.9], [0.84, 0.44, 0.12]),
    cyl('metal', [0.5, 0.28, 0.5], [0.16, 0.44, 0.16]),
    cyl('dark', [0.5, 0.04, 0.5], [0.96, 0.08, 0.96])] }],
  ['plant-large', { model: 'potted-plant', parts: plant(0.26) }],
  ['potted-plant', { model: 'plant-small-1', parts: plant(0.34) }],
  ['lamp-floor', { model: 'lamp-round-floor', parts: lamp(0.24) }],

  /* --- 대형 ---------------------------------------------------------------------- */
  ['shelf-tall', { model: 'shelf-tall', parts: openShelf(4) }],
  ['bookcase-open', { model: 'bookcase-open', parts: openShelf(5) }],
  ['wardrobe', { model: 'bookcase-closed-wide', parts: cabinet(2, 0.1) }],
  ['coat-rack', { model: 'coat-rack-standing', parts: [
    cyl('dark', [0.5, 0.03, 0.5], [0.9, 0.06, 0.9]),
    cyl('main', [0.5, 0.5, 0.5], [0.16, 1, 0.16]),
    ...crossArms('main', 0.88, 0.44, 0.08)] }],
  ['fridge', { model: 'kitchen-fridge', parts: [
    box('main', [0.5, 0.5, 0.44], [1, 1, 0.88]),
    box('light', [0.5, 0.72, 0.94], [0.92, 0.5, 0.12]),
    box('light', [0.5, 0.24, 0.94], [0.92, 0.42, 0.12]),
    box('metal', [0.12, 0.6, 0.92], [0.05, 0.46, 0.1])] }],
  ['sofa', { model: 'lounge-sofa', parts: [
    box('main', [0.5, 0.34, 0.54], [1, 0.5, 0.92]),
    box('main', [0.5, 0.72, 0.88], [1, 0.56, 0.24]),
    box('main', [0.05, 0.6, 0.54], [0.1, 0.5, 0.92]),
    box('main', [0.95, 0.6, 0.54], [0.1, 0.5, 0.92]),
    box('light', [0.29, 0.64, 0.5], [0.36, 0.16, 0.62]),
    box('light', [0.71, 0.64, 0.5], [0.36, 0.16, 0.62])] }],
  ['bed-double', { model: 'bed-double', parts: [
    box('main', [0.5, 0.28, 0.55], [1, 0.56, 0.9]),
    box('light', [0.5, 0.66, 0.56], [0.94, 0.24, 0.86]),
    box('main', [0.5, 0.68, 0.04], [1, 0.64, 0.08]),
    box('light', [0.28, 0.84, 0.16], [0.32, 0.14, 0.14]),
    box('light', [0.72, 0.84, 0.16], [0.32, 0.14, 0.14])] }],
  ['bathtub', { model: 'bathtub', parts: [
    box('main', [0.5, 0.42, 0.5], [1, 0.84, 1]),
    box('light', [0.5, 0.76, 0.5], [0.86, 0.36, 0.82]),
    box('metal', [0.06, 0.9, 0.5], [0.1, 0.18, 0.16])] }],
  ['rug-round', { model: 'rug-round', parts: [
    cyl('main', [0.5, 0.4, 0.5], [1, 0.8, 1]),
    cyl('light', [0.5, 0.86, 0.5], [0.64, 0.28, 0.64])] }],
  ['rug-rectangle', { model: 'rug-rectangle', parts: [
    box('main', [0.5, 0.4, 0.5], [1, 0.8, 1]),
    box('light', [0.5, 0.86, 0.5], [0.8, 0.28, 0.7])] }],
])

/** 레시피 조회. 없으면 `undefined` - 호출부가 기본 형상으로 대체한다. */
export function getShapeRecipe(catalogId: CatalogId): ShapeRecipe | undefined {
  return SHAPE_RECIPES.get(catalogId)
}

/* --- 공유 자원 캐시 -------------------------------------------------------------- */

/**
 * 공유 자원 표식. 이 플래그가 붙은 노드의 자원은 **절대 dispose 하면 안 된다** -
 * 오브젝트 하나를 지울 때 공유 박스 지오메트리를 해제하면 방 전체가 빈 메시가 된다.
 */
const SHARED_RESOURCE_KEY = 'meshFactoryShared'

/** 판정 전용 상자 표식. 그림자 설정이 이 표식을 보고 건너뛴다(보이지 않는 상자가 그림자를 드리우면 안 된다). */
const HIT_COLLIDER_KEY = 'meshFactoryHitCollider'

/** 슬롯별 재질 특성. 반사는 가독성을 해치므로 metalness는 전부 0 근처다. */
const SURFACE_ROUGHNESS = 0.92
const METAL_ROUGHNESS = 0.5
const METAL_METALNESS = 0.08

/** 팔레트 색에서 슬롯 색을 만들 때의 혼합 비율. */
const DARK_MIX = 0.42
const LIGHT_MIX = 0.4
/** 이 휘도보다 어두운 몸통에는 밝은 표식을, 밝은 몸통에는 어두운 표식을 붙인다. */
const MARKER_LUMINANCE_THRESHOLD = 0.25
const MARKER_CONTRAST_MIX = 0.55

const FIXED_METAL_COLOR = 0x9ba4a7
const FIXED_LEAF_COLOR = 0x7d9c63
const WHITE = 0xffffff
const BLACK = 0x000000

/** 저폴리 카툰 룩의 분할 수. 실사 곡면이 아니라 각진 면이 보이는 편이 실루엣 판독에 낫다. */
const CYLINDER_SEGMENTS = 12
const SPHERE_WIDTH_SEGMENTS = 12
const SPHERE_HEIGHT_SEGMENTS = 8

const geometryCache = new Map<PartShape, BufferGeometry>()
/**
 * 슬롯별 깊이 바이어스(polygonOffset). 음수가 카메라 쪽으로 당긴다.
 *
 * Kenney 에셋에는 **간격이 0인 동일평면 면**이 들어 있다 - 상자의 테이프, 서랍 안쪽,
 * 문짝에 붙은 손잡이 판 같은 디테일이 본체 면과 정확히 같은 평면에 놓인다.
 * 깊이 버퍼 정밀도를 아무리 올려도(near:far를 1:2272에서 1:6.3으로 좁혔다) **같은 깊이값은
 * 갈라지지 않으므로** 카메라가 움직일 때마다 어느 면이 이기는지 뒤바뀌며 자글거린다.
 *
 * 그래서 정밀도가 아니라 **순서**로 푼다. 슬롯마다 다른 바이어스를 주면 겹친 두 면이
 * 서로 다른 슬롯인 한 렌더 순서가 고정된다(디테일은 대개 본체와 다른 머티리얼을 쓴다).
 * 값이 작은 이유는 크게 주면 이웃한 다른 오브젝트와의 앞뒤가 뒤집히기 때문이다.
 *
 * `marker`가 가장 앞인 것은 이름 그대로 표식이라 언제나 본체를 덮어야 하기 때문이다.
 */
const SLOT_DEPTH_BIAS: Record<PartSlot, number> = {
  main: 0,
  dark: -1,
  light: -2,
  metal: -3,
  leaf: -4,
  marker: -5,
}

const materialCache = new Map<string, MeshStandardMaterial>()
const modelTemplates = new Map<CatalogId, Object3D>()
/** 판정 상자 전용 머티리얼. 씬 전체가 하나를 공유한다. */
let colliderMaterial: MeshBasicMaterial | undefined

/** 판정 전용 상자인가. 그림자·색 반영 경로가 건너뛰어야 하는 노드다. */
export function isHitCollider(node: Object3D): boolean {
  return node.userData[HIT_COLLIDER_KEY] === true
}

/** 크기 1의 단위 지오메트리. 실제 크기는 전부 `mesh.scale`이 낸다. */
export function getUnitGeometry(shape: PartShape): BufferGeometry {
  const cached = geometryCache.get(shape)
  if (cached !== undefined) return cached

  const created = createUnitGeometry(shape)
  geometryCache.set(shape, created)
  return created
}

function createUnitGeometry(shape: PartShape): BufferGeometry {
  if (shape === 'box') return new BoxGeometry(1, 1, 1)
  if (shape === 'sphere') return new SphereGeometry(0.5, SPHERE_WIDTH_SEGMENTS, SPHERE_HEIGHT_SEGMENTS)
  return new CylinderGeometry(0.5, 0.5, 1, CYLINDER_SEGMENTS)
}

/**
 * (슬롯 × colorId) 머티리얼. 최대 60개이며 씬 전체가 공유한다.
 * 공유물이므로 색을 제자리 변경하면 안 된다 - 같은 조합의 오브젝트가 전부 같이 바뀌어
 * "한 물건만" 바뀌어야 하는 색 변경 동사(5.1절)가 방 전체 변화가 된다. 참조를 갈아 끼운다.
 */
export function getSlotMaterial(slot: PartSlot, colorId: PaletteIndex): MeshStandardMaterial {
  const key = `${slot}:${colorId}`
  const cached = materialCache.get(key)
  if (cached !== undefined) return cached

  const created = new MeshStandardMaterial({
    color: computeSlotColor(slot, colorId),
    roughness: slot === 'metal' ? METAL_ROUGHNESS : SURFACE_ROUGHNESS,
    metalness: slot === 'metal' ? METAL_METALNESS : 0,
    flatShading: true,
    // 동일평면 z-fighting 방지 - SLOT_DEPTH_BIAS 주석 참고.
    polygonOffset: true,
    polygonOffsetFactor: SLOT_DEPTH_BIAS[slot],
    polygonOffsetUnits: SLOT_DEPTH_BIAS[slot],
  })
  materialCache.set(key, created)
  return created
}

/** 슬롯 색 계산. 범위 밖 `colorId`는 `getColor()`가 던진다(조용한 기본색 대체 금지). */
export function computeSlotColor(slot: PartSlot, colorId: PaletteIndex): Color {
  const base = getColor(colorId)

  if (slot === 'metal') return new Color(FIXED_METAL_COLOR)
  if (slot === 'leaf') return new Color(FIXED_LEAF_COLOR)
  if (slot === 'dark') return new Color(base.hex).lerp(new Color(BLACK), DARK_MIX)
  if (slot === 'light') return new Color(base.hex).lerp(new Color(WHITE), LIGHT_MIX)
  if (slot === 'marker') {
    const target = new Color(base.luminance < MARKER_LUMINANCE_THRESHOLD ? WHITE : BLACK)
    return new Color(base.hex).lerp(target, MARKER_CONTRAST_MIX)
  }
  return new Color(base.hex)
}

/* --- 메시 생성 ------------------------------------------------------------------- */

const USER_DATA_SLOT = 'slot'

/** 공유 자원을 쓰는 메시로 표시한다. `disposeObject3D`가 이 표식을 보고 건너뛴다. */
export function markShared(node: Object3D): void {
  node.userData[SHARED_RESOURCE_KEY] = true
}

/** 공유 자원을 쓰는 노드인가. 해제 경로가 반드시 물어야 하는 질문이다. */
export function isSharedResource(node: Object3D): boolean {
  return node.userData[SHARED_RESOURCE_KEY] === true
}

/** 공유 지오메트리 + 공유 머티리얼로 메시 하나를 만든다. 새로 할당되는 것은 껍데기뿐이다. */
export function createSharedMesh(shape: PartShape, slot: PartSlot, colorId: PaletteIndex): Mesh {
  const mesh = new Mesh(getUnitGeometry(shape), getSlotMaterial(slot, colorId))
  mesh.userData[USER_DATA_SLOT] = slot
  markShared(mesh)
  return mesh
}

/**
 * 소품의 몸통을 만든다. 루트는 `Group`이고 원점은 **footprint 박스의 중심**이다.
 * GLB 템플릿이 있으면 복제하고(지오메트리 공유) 없으면 절차적 파트로 그린다 -
 * 폴백이 분기 한 줄인 것은 두 경로가 같은 규약(중심 원점 · footprint 안쪽)을 지키기 때문이다.
 */
export function createObjectBody(entry: CatalogEntry, colorId: PaletteIndex): Object3D {
  const template = modelTemplates.get(entry.id)
  const body = template === undefined ? createProceduralBody(entry, colorId) : template.clone(true)

  body.name = `body:${entry.id}`
  applyBodyColor(body, colorId)
  body.add(createHitCollider(entry))
  return body
}

/**
 * 점유 셀 전체를 덮는 **보이지 않는 판정용 상자**.
 *
 * 왜 형상 그대로 판정하지 않는가 - 탄환 경제 때문이다(컨셉시트 4.3: 오답 1회 = 1발).
 * 실루엣대로 판정하면 의자의 얇은 등받이를 2cm 빗나간 조준이 탄환 한 발이 된다.
 * 6.2절이 제거 판정에서 경고한 "억울한 실패"가 오브젝트 히트에서 재현되는 셈이라,
 * **점유 셀 안이면 어디를 맞혀도 그 물건을 맞힌 것**으로 친다. 3.1절의 "한 셀에 하나"가
 * 이 관대함을 안전하게 만든다 - 남의 점유 상자 안에 다른 물건이 들어올 수 없으므로
 * 넉넉한 상자가 **다른 물건을 가로채는 일은 원리적으로 없다.**
 * 대가는 식탁 다리 사이로 저 너머를 저격하지 못하는 것 하나이며, 걸어서 각을 바꾸면 된다.
 *
 * 구현 주의: `object.visible`이 아니라 **머티리얼의 `visible`을 끈다.**
 * 전자로 끄면 `tapPicker.ts`의 가시성 검사(`isEffectivelyVisible` - 숨은 배치 고스트가 판정을 막지 않게 하는 장치)가
 * 이 상자까지 통과시켜 버린다. 머티리얼을 끄면 렌더 목록에서만 빠지고 판정은 살아 있다.
 */
function createHitCollider(entry: CatalogEntry): Mesh {
  const collider = new Mesh(getUnitGeometry('box'), getColliderMaterial())
  collider.scale.set(
    entry.footprint.x * CELL_SIZE_METERS,
    entry.footprint.y * CELL_SIZE_METERS,
    entry.footprint.z * CELL_SIZE_METERS,
  )
  collider.userData[HIT_COLLIDER_KEY] = true
  markShared(collider)
  return collider
}

function getColliderMaterial(): MeshBasicMaterial {
  colliderMaterial ??= new MeshBasicMaterial({ visible: false })
  return colliderMaterial
}

/** 절차적 형상. 레시피가 없는 소품은 footprint를 꽉 채운 박스 + 정면 패널로 그린다. */
function createProceduralBody(entry: CatalogEntry, colorId: PaletteIndex): Object3D {
  const group = new Group()
  const meters = {
    x: entry.footprint.x * CELL_SIZE_METERS,
    y: entry.footprint.y * CELL_SIZE_METERS,
    z: entry.footprint.z * CELL_SIZE_METERS,
  }

  for (const part of getShapeRecipe(entry.id)?.parts ?? FALLBACK_PARTS) {
    const mesh = createSharedMesh(part.shape, part.slot, colorId)
    mesh.scale.set(part.size.x * meters.x, part.size.y * meters.y, part.size.z * meters.z)
    mesh.position.set(
      (part.center.x - 0.5) * meters.x,
      (part.center.y - 0.5) * meters.y,
      (part.center.z - 0.5) * meters.z,
    )
    group.add(mesh)
  }

  return group
}

/**
 * 레시피가 없는 소품의 최후 형상. 정면(+z)에 패널이 있으므로 회전이 보인다.
 * 무늬 없는 정육면체로 두면 `rotSymmetry: 1` 선언이 거짓이 되기 때문에 패널은 장식이 아니다.
 */
const FALLBACK_PARTS: readonly PartSpec[] = [
  box('main', [0.5, 0.5, 0.46], [1, 1, 0.92]),
  box('light', [0.5, 0.5, 0.96], [0.6, 0.6, 0.08]),
]

/** 몸통 색을 맞춘다. 공유 머티리얼이므로 색을 덮어쓰지 않고 **참조를 교체**한다. */
export function applyBodyColor(body: Object3D, colorId: PaletteIndex): void {
  body.traverse((node) => {
    if (!(node instanceof Mesh)) return
    const slot = readSlot(node)
    if (slot === undefined) return
    node.material = getSlotMaterial(slot, colorId)
  })
}

function readSlot(node: Object3D): PartSlot | undefined {
  const value: unknown = node.userData[USER_DATA_SLOT]
  return typeof value === 'string' ? (value as PartSlot) : undefined
}

/**
 * 몸통에서 **실제로 보이는 부품**만의 로컬 바운딩 박스.
 *
 * 판정 상자는 언제나 footprint 크기라 같이 재면 무의미해지므로 제외한다.
 * 호출부(objectView)는 이 값으로 방향 표식을 몸통에 붙인다 - GLB는 실제 치수를 유지해서
 * 머그컵처럼 셀보다 한참 작은 소품이 흔한데, 표식을 셀 경계에 두면 아무것에도 붙지 않은
 * 색 조각이 공중에 뜬다. 틀린그림찾기에서 그건 노이즈가 아니라 함정이다.
 */
export function measureBodyExtent(body: Object3D): {
  min: { x: number; y: number; z: number }
  size: { x: number; y: number; z: number }
} {
  const bounds = new Box3()
  body.updateMatrixWorld(true)
  body.traverse((node) => {
    if (node instanceof Mesh && !isHitCollider(node)) bounds.expandByObject(node)
  })

  if (bounds.isEmpty()) return { min: { x: 0, y: 0, z: 0 }, size: { x: 0, y: 0, z: 0 } }

  const size = bounds.getSize(new Vector3())
  return {
    min: { x: bounds.min.x, y: bounds.min.y, z: bounds.min.z },
    size: { x: size.x, y: size.y, z: size.z },
  }
}

/** 캐시 전체를 해제한다. **씬을 통째로 버릴 때만** - 소품 하나를 지울 때 부르면 방이 비어 버린다. */
export function disposeMeshFactory(): void {
  for (const geometry of geometryCache.values()) geometry.dispose()
  for (const material of materialCache.values()) material.dispose()
  for (const template of modelTemplates.values()) {
    template.traverse((node) => {
      if (node instanceof Mesh) node.geometry.dispose()
    })
  }
  colliderMaterial?.dispose()
  colliderMaterial = undefined
  geometryCache.clear()
  materialCache.clear()
  modelTemplates.clear()
}

/* --- GLB 프리로드 ---------------------------------------------------------------- */

/** 로드 결과. 실패를 **조용히 삼키지 않고** 호출부에 돌려준다. */
export type ModelLoadReport = Readonly<{
  loaded: readonly CatalogId[]
  /** 실패한 소품과 이유. 이 소품들은 절차적 형상으로 그려진다. */
  failed: readonly Readonly<{ catalogId: CatalogId; reason: string }>[]
}>

type ModelManifest = Readonly<{
  models: Readonly<Record<string, Readonly<{ scale: number }> | undefined>>
}>

const DEFAULT_ASSET_BASE_URL = '/assets'

/**
 * 카탈로그 소품의 GLB를 미리 읽어 템플릿으로 만든다.
 *
 * **부르지 않아도 게임은 돌아간다** - 그때는 전부 절차적 형상이다.
 * 실패한 소품은 조용히 삼키지 않고 보고서에 담아 돌려준다.
 *
 * 스케일 계약(ASSETS.md 2절): 두 Kenney 키트는 저작 단위가 달라 `models.json`의
 * `scale`을 반드시 적용해야 한다. 그 뒤 footprint에 맞춰 **균등 축소**한다 -
 * 균등이라야 비율이 안 망가지고, 축소라야 메시가 점유 셀 밖으로 나가지 않는다.
 */
export async function preloadCatalogModels(
  baseUrl: string = DEFAULT_ASSET_BASE_URL,
): Promise<ModelLoadReport> {
  const loaded: CatalogId[] = []
  const failed: { catalogId: CatalogId; reason: string }[] = []

  let manifest: ModelManifest
  try {
    const response = await fetch(`${baseUrl}/models.json`)
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    manifest = (await response.json()) as ModelManifest
  } catch (error: unknown) {
    // 매니페스트가 없으면 스케일 계약을 지킬 수 없다 → 전량 절차적 폴백.
    return {
      loaded: [],
      failed: CATALOG.map((entry) => ({ catalogId: entry.id, reason: describeError(error) })),
    }
  }

  const loader = new GLTFLoader()

  await Promise.all(
    CATALOG.map(async (entry) => {
      // 불러올 GLB의 출처는 **카탈로그의 `modelId` 하나뿐이다.**
      //
      // 예전에는 `getShapeRecipe(entry.id)?.model`을 봤는데, 레시피 표는 절차적 형상을 손으로 적은
      // 44종밖에 없다. 카탈로그가 151종으로 늘어난 뒤에도 그 경로를 그대로 두면
      // **레시피가 없는 107종은 GLB 요청조차 나가지 않고** 전부 폴백 박스로 그려진다 -
      // 카탈로그를 늘린 작업 전체가 화면에 한 픽셀도 반영되지 않는 상태였다.
      //
      // 레시피의 `model` 필드는 이 값과 중복이며(44종 전부 일치함을 확인했다) 더 이상 로딩에 쓰지 않는다.
      // 진실의 원천이 둘이면 반드시 갈라지고, 이번 버그가 정확히 그 사고였다.
      const modelName = entry.modelId

      try {
        const scale = manifest.models[modelName]?.scale
        if (scale === undefined) throw new Error(`매니페스트에 '${modelName}' 없음`)

        const gltf = await loader.loadAsync(`${baseUrl}/models/${modelName}.glb`)
        modelTemplates.set(entry.id, buildTemplate(gltf.scene, entry, scale))
        loaded.push(entry.id)
      } catch (error: unknown) {
        failed.push({ catalogId: entry.id, reason: describeError(error) })
      }
    }),
  )

  return { loaded, failed }
}

function describeError(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

/**
 * 로드된 씬을 footprint 박스에 맞춘 템플릿으로 굽는다.
 * 원점은 박스 중심, 바닥은 박스 바닥에 붙인다 - 절차적 형상과 같은 규약이라
 * 두 경로가 화면에서 구별되지 않는다.
 *
 * x/z를 **가운데 정렬**하는 것은 취향이 아니라 요건이다. 한쪽으로 붙여 놓으면
 * `rotSymmetry: 4` 소품을 90° 돌렸을 때 물건이 셀 안에서 움직여 버리고,
 * 그건 "회전이 안 보인다"는 서버의 판단(4.2절)과 정면으로 어긋난다.
 */
function buildTemplate(scene: Object3D, entry: CatalogEntry, manifestScale: number): Object3D {
  const holder = new Group()
  const meters = {
    x: entry.footprint.x * CELL_SIZE_METERS,
    y: entry.footprint.y * CELL_SIZE_METERS,
    z: entry.footprint.z * CELL_SIZE_METERS,
  }

  scene.scale.setScalar(manifestScale)
  const raw = new Box3().setFromObject(scene).getSize(new Vector3())
  // 넘칠 때만 줄이고 **절대 키우지 않는다**(상한 1).
  // 매니페스트 스케일이 이미 실제 치수를 뜻하기 때문이다(ASSETS.md 2절: 머그컵 = 9.6cm).
  // footprint는 그 물건이 차지하는 **예약 공간**이지 목표 크기가 아니라서,
  // 채우려고 늘리면 머그컵이 25cm가 되어 소파 옆에서 스케일이 무너진다.
  // 남는 여백 때문에 조준이 빡빡해지는 문제는 판정 상자가 이미 막아 준다.
  const fit = Math.min(1, meters.x / raw.x, meters.y / raw.y, meters.z / raw.z)
  scene.scale.setScalar(manifestScale * fit)

  const fitted = new Box3().setFromObject(scene)
  scene.position.set(
    -(fitted.min.x + fitted.max.x) / 2,
    -meters.y / 2 - fitted.min.y,
    -(fitted.min.z + fitted.max.z) / 2,
  )

  assignModelSlots(scene)
  holder.add(scene)
  return holder
}

/**
 * GLB 머티리얼 이름을 색 슬롯으로 매핑한다(ASSETS.md 3.1절의 의미 슬롯).
 *
 * 전 슬롯을 팔레트색으로 칠하면 물건이 단색 덩어리가 되므로 **지배적인 슬롯 하나만 `main`**으로
 * 삼고 나머지는 파생색·고정색으로 보낸다. 못 맞히면 색이 조금 이상해질 뿐 판정에는 영향이 없다.
 */
function assignModelSlots(scene: Object3D): void {
  const names = collectMaterialNames(scene)
  const mainName = pickMainMaterialName(names)

  scene.traverse((node) => {
    if (!(node instanceof Mesh)) return
    node.userData[USER_DATA_SLOT] = resolveModelSlot(materialName(node), mainName)
    markShared(node)
  })
}

function materialName(mesh: Mesh): string {
  const material = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material
  return material?.name ?? ''
}

function collectMaterialNames(scene: Object3D): readonly string[] {
  const names: string[] = []
  scene.traverse((node) => {
    if (node instanceof Mesh) names.push(materialName(node))
  })
  return names
}

/**
 * 주색으로 삼을 머티리얼 이름을 고른다.
 * 우선순위: 천 → 변환 모델의 지배색(`slot0`) → 원목 → 기본. 없으면 첫 번째 것을 쓴다.
 * 순수 함수로 떼어 둔 이유는 이름 규칙이 틀리기 쉬운 부분이라 테스트를 붙이기 위해서다.
 */
export function pickMainMaterialName(names: readonly string[]): string {
  const prefixes = ['carpet', 'slot0', 'wood', '_defaultMat']
  for (const prefix of prefixes) {
    const hit = names.find((name) => name.startsWith(prefix))
    if (hit !== undefined) return hit
  }
  return names[0] ?? ''
}

/** GLB 머티리얼 이름 → 색 슬롯. 못 맞혀도 색이 조금 이상해질 뿐 판정에는 영향이 없다. */
export function resolveModelSlot(name: string, mainName: string): PartSlot {
  if (name === mainName) return 'main'
  if (name === 'plant' || name === 'fur') return 'leaf'
  if (name.startsWith('metal') || name === 'glass') return 'metal'
  if (name.includes('Dark') || name.startsWith('wood')) return 'dark'
  return 'light'
}
